import { motion } from 'framer-motion'
import { IconButton, SHELL, SPRING_PANEL, SidebarToggleMark, cn } from '@workflow/ui'
import { NotificationsMenu } from './NotificationsMenu'

/**
 * The strip over the page.
 *
 * Not a bar: no fill, no hairline. It is a transparent layer over the page
 * surface, and the page scrolls underneath it behind a gradient. Its two
 * residents are the sidebar toggle and the notification bell — one for the
 * room's shape, one for its business — both unable to scroll away.
 *
 * On a narrow window the sidebar is a drawer over the page, and the strip is
 * page furniture: its toggle would sit under the drawer's brand band. While
 * the drawer is out, the toggle rides the drawer's own spring to its right
 * edge — the same place it occupies when the column is resident — so the
 * control that opens and closes the column is always exactly where the hand
 * learned to find it.
 */
export function TopStrip({
  compact,
  sidebarOpen,
  onToggle,
}: {
  /** The window is narrow: the sidebar floats over the page rather than beside it. */
  compact: boolean
  /** Whether the column is on screen — the toggle says so with its active state. */
  sidebarOpen: boolean
  onToggle: () => void
}) {
  const drawerOut = compact && sidebarOpen

  return (
    <header
      className={cn('absolute inset-x-0 top-0 z-30 flex items-center px-3', drawerOut && 'pointer-events-none')}
      style={{ height: SHELL.topStripHeight }}
    >
      <motion.div
        animate={{ x: drawerOut ? SHELL.sidebarWidth : 0 }}
        transition={SPRING_PANEL}
        className={cn('flex shrink-0 items-center', drawerOut && 'pointer-events-auto')}
      >
        <IconButton
          label={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
          tooltipSide="bottom"
          active={sidebarOpen}
          onClick={onToggle}
        >
          <SidebarToggleMark open={sidebarOpen} size="var(--icon-md)" />
        </IconButton>
      </motion.div>

      {/* The bell rides the strip's far end — the page's own sky, above the
          scroll and away from the column's edge. */}
      <div className="ml-auto flex items-center">
        <NotificationsMenu />
      </div>
    </header>
  )
}
